import React, { useEffect, useRef, useState } from "react";

import { Tooltip } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import ShipeeziGrid, { Constants } from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";

import { PropHandles } from "../../../../../../../../components/Generic/ViewJobComponent";
import { SidebarViewOrder } from "../../../../../../../../components/SidebarViewOrder";
import { useAppState } from "../../../../../../../../context/StateContext";
import { Job } from "../../../../../../../../models/job";
import { JobType } from "../../../../../../../../models/jobType";
import { Filters, Searches, Sort } from "../../../../../../../../models/shipeezi.table";
import { Statuses } from "../../../../../../../../models/statuses";
import JobService from "../../../../../../../../services/job.service";
import JobTypeService from "../../../../../../../../services/job.type.service";
import StatusesService from "../../../../../../../../services/statuses.service";
import { CACHE_TIME, STALE_TIME } from "../../../../../../../../util/constants";
import { Status, SubStatusUsedBy } from "../../../../../../../../util/enums";
import { getMeasurement } from "../../../../../../../../util/getMeasurement";
import { handlesGrid } from "../../../../../../../../util/handlesGrid";
import { FiEyeButton } from "../../../../Configurations/NotifyParty/styles";
import { useSubcontractorContext } from "../context/Subcontractor";
import { getOrdersTableColumns } from "./getOrdersTableColumns";
import { NoDataContainer, NoDataTitle } from "./styles";

export const Orders = () => {
  const {
    subcontractor: { id: subcontractorId },
  } = useSubcontractorContext();
  const { currentUser } = useAppState();

  const { t: translate } = useTranslation();

  const [disableCustomerSorting, setDisableCustomerSorting] = useState<boolean>(false);
  const [disableOriginSorting, setDisableOriginSorting] = useState<boolean>(false);
  const [disableDestinationSorting, setDisableDestinationSorting] = useState<boolean>(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [filters, setFilters] = useState<Filters>();
  const [searches, setSearches] = useState<Searches>([]);
  const [sorting, setSorting] = useState<Array<string[]>>();
  const [jobTypes, setJobTypes] = useState<JobType[]>([]);
  const [jobStatus, setJobStatus] = useState<Statuses[]>([]);
  const [toViewOrder, setToViewOrder] = useState<Job>();
  const [orders, setOrders] = useState<Job[]>([]);
  const [countOrders, setCountOrders] = useState<number>(0);

  const sideViewRef = useRef<PropHandles>(null);

  const measurement = getMeasurement();
  const { handleCurrentPage, handlePageSize, handleSort, handleFilter } = handlesGrid({
    setFilters,
    setSorting,
    setPageSize,
    setPage,
  });

  const handleSearch = (values: Searches) => {
    if (setSearches) setSearches(values);
    if (setPage) setPage(0);

    return values;
  };

  const columns = getOrdersTableColumns({
    translate,
    disableCustomerSorting,
    disableDestinationSorting,
    disableOriginSorting,
    measurement,
    setDisableCustomerSorting,
    setDisableDestinationSorting,
    setDisableOriginSorting,
    setSorting,
  });

  const renderGridLoadingStatus = () => {
    if (loading) {
      return `${translate("loadingQuotes")}...`;
    }

    return (
      <NoDataContainer>
        <NoDataTitle>{translate("noInformationToBeShown")}</NoDataTitle>
      </NoDataContainer>
    );
  };

  const renderStatusOfData = () => {
    return renderGridLoadingStatus();
  };

  const handleViewButton = (id: string | number, order: Job) => {
    return (
      <Tooltip data-cy="action-view" title={translate("view")} placement="top">
        <div>
          <FiEyeButton
            onClick={() => {
              setToViewOrder(order);
              sideViewRef.current?.setOpenSideBar(true);
            }}
            color="#475467"
            size={Constants.defaultGeneralActionsIconSize}
          />
        </div>
      </Tooltip>
    );
  };

  useQuery(
    ["jobType.list"],
    () =>
      JobTypeService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        sortBy: JSON.stringify([["name", "asc"]]),
      }),
    {
      refetchOnWindowFocus: false,
      enabled: !!currentUser?.companyId,
      onSuccess: (response) => {
        if (response && response.data && response.data.length > 0) setJobTypes(response.data);
      },
    }
  );

  useQuery(
    ["statuses.list.order"],
    () =>
      StatusesService.getAll({
        status: Status.ACTIVE,
        companyId: currentUser?.companyId,
        usedBy: SubStatusUsedBy.ORDERS,
      }),
    {
      enabled: !!currentUser?.companyId,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data && response.data.length > 0) {
          setJobStatus(response.data);
        }
      },
    }
  );

  const { data: dataOrders, isFetching: loading } = useQuery(
    ["orders.by.subcontractor.list", page, pageSize, sorting, filters, searches],
    async () => {
      const response = await JobService.getAllAndCountBySubcontractor({
        attributes: JSON.stringify([
          "id",
          "reference",
          "type",
          "mode",
          "volume",
          "updatedAt",
          "createdAt",
          "customVolumeActual",
          "status",
          "leadSourceFrom",
        ]),
        companyId: currentUser?.companyId,
        subcontractorId,
        page,
        pageSize,
        sortBy:
          sorting && sorting?.length > 0
            ? JSON.stringify(sorting)
            : JSON.stringify([["updatedAt", "DESC"]]),
        include: JSON.stringify([
          {
            required: false,
            association: "origins",
            attributes: ["id", "longAddress", "createdAt"],
            order: ["createdAt", "desc"],
            through: {
              attributes: [],
            },
          },
          {
            required: false,
            association: "destinations",
            attributes: ["id", "longAddress", "createdAt"],
            order: ["createdAt", "desc"],
            through: {
              attributes: [],
            },
          },
          {
            association: "customer",
            required: false,
            attributes: ["id", "fullName"],
          },
          {
            association: "itemsJob",
            attributes: ["id", "quantity", "volume", "status", "subStatus"],
            include: [
              { association: "destination" },
              {
                association: "mapCells",
                attributes: ["id", "name", "mapId"],
                include: [
                  {
                    association: "map",
                    attributes: ["id", "warehouseId", "sectionId", "name"],
                    include: [
                      { association: "warehouse", attributes: ["id", "name"] },
                      { association: "section", attributes: ["id", "name"] },
                    ],
                  },
                ],
              },
            ],
          },
          {
            association: "storageBoxes",
            attributes: ["id", "number", "storageBoxTypeId"],
            include: [
              {
                association: "storageBoxType",
                attributes: ["id", "name"],
              },
            ],
          },
          {
            association: "materials",
            attributes: ["id", "volume", "quantity"],
            include: [
              {
                association: "serviceBooking",
                attributes: ["id", "isItem"],
              },
              {
                association: "destination",
                attributes: ["id", "name"],
              },
            ],
          },
        ]),
        searchByOr: searches && searches?.length > 0 ? JSON.stringify(searches) : "",
        ...filters,
      });

      if (response?.data) return response.data;

      return {
        count: 0,
        rows: [],
      };
    },
    {
      enabled: !!currentUser?.id && !!subcontractorId,
      refetchOnWindowFocus: false,
      staleTime: STALE_TIME,
      cacheTime: CACHE_TIME,
    }
  );

  useEffect(() => {
    if (dataOrders?.rows) {
      setOrders(dataOrders.rows);
    }
    if (dataOrders?.count !== null && dataOrders?.count !== undefined) {
      setCountOrders(dataOrders?.count);
    }
  }, [dataOrders]);

  return (
    <>
      <ShipeeziGrid
        columns={columns}
        data={orders || []}
        totalItems={countOrders}
        loadingData={loading}
        namePage={translate("orders")}
        emptyData={renderStatusOfData}
        pageSize={pageSize}
        pageSizeList={[20, 40, 60, 100]}
        onChangePageSize={handlePageSize}
        currentPage={page}
        onNextPage={handleCurrentPage}
        onPreviousPage={handleCurrentPage}
        onGoToPage={handleCurrentPage}
        generalActions={{
          orderBy: { left: ["handleSearch", "handleFilterBy"] },
          handleSearch: {
            onSearch: (filters) => handleSearch(filters),
          },
          handleFilterBy: {
            onChange: handleFilter,
            categories: [
              {
                name: translate("type"),
                options: jobTypes && jobTypes?.map((jobType) => jobType?.name || ""),
              },
              {
                name: translate("status"),
                options: jobStatus?.map((jobStatus) => jobStatus?.name || ""),
              },
            ],
          },
        }}
        positionActionsColumn="right"
        rowActionsExtra={[
          {
            title: translate("view"),
            key: "view",
            buttonAndAction: handleViewButton,
          },
        ]}
        onSorting={(sorts: Sort[]) => handleSort(sorts)}
      />

      <SidebarViewOrder
        jobId={toViewOrder?.id}
        sideViewRef={sideViewRef}
        onCloseModal={() => setToViewOrder(undefined)}
      />
    </>
  );
};
